import type { LocaleParams } from "@magic-md/i18n";
import {
    Activity,
    Clipboard,
    Copy,
    Eye,
    ExternalLink,
    FileText,
    FolderTree,
    Link,
    Package,
    PanelRightOpen,
    Star,
    StarOff,
    Upload,
    X,
    XCircle,
} from "lucide-react";
import { EXTERNAL_OPENERS, REVEAL_IN_FOLDER } from "../lib/external-openers";
import { revealPathInExplorer } from "../lib/explorer-reveal";
import { fileNameFromPath } from "../lib/path-utils";
import {
    getDocumentAssetsDir,
    getDocumentBundleDir,
} from "../lib/publish-utils";
import {
    hasKnownDocumentPublishPreview,
    previewDocumentPublish,
} from "../lib/publish-preview";
import { getPlatformService, type PlatformService } from "../platform";
import { useEditorStore } from "../store/editor-store";
import { useLogStore } from "../store/log-store";
import {
    ensureTraceNotificationHandlers,
    useTraceStore,
} from "../store/trace-store";
import { buildExtensionContextMenuItems } from "../extensions/extension-menu-bridge";
import { buildVirtualPackageMenuContext } from "../extensions/virtual-package-menu-context";
import type { MenuContext } from "@magic-md/extension-sdk";
import type { ContextMenuItem } from "./ContextMenu";
import { showDocumentPublishDialog } from "./dialogs/DocumentPublishDialog";

export type EditorTranslator = (
    key: string,
    params?: LocaleParams,
) => string;

/** Extra menu items that platform hosts (e.g. Tauri Editor) can inject. */
let extraTabMenuItems: ((tabId: string) => ContextMenuItem[]) | null = null;

/**
 * Register a callback that provides additional context menu items for tabs.
 * Called by the Editor app to add "Move to New Window" etc.
 */
export function setTabContextMenuExtras(
    fn: ((tabId: string) => ContextMenuItem[]) | null,
): void {
    extraTabMenuItems = fn;
}

function getOptionalOpenExternal(): PlatformService["openExternal"] | undefined {
    try {
        return getPlatformService().openExternal;
    } catch {
        return undefined;
    }
}

export function getWorkspaceRelativePath(filePath: string): string {
    const normalizedFilePath = filePath.replace(/\\/g, "/");
    const roots = useEditorStore
        .getState()
        .workspaceFolders.map((folder) => {
            const normalizedRoot = folder.path.replace(/\\/g, "/").replace(/\/+$/, "");
            return normalizedRoot || (folder.path.match(/^[/\\]/) ? "/" : "");
        })
        .filter(Boolean)
        .sort((a, b) => b.length - a.length);
    const root = roots.find((candidate) => {
        if (candidate === "/") return normalizedFilePath.startsWith("/");
        return (
            normalizedFilePath === candidate ||
            normalizedFilePath.startsWith(`${candidate}/`)
        );
    });
    if (!root) return filePath;
    return (
        normalizedFilePath.slice(root.length).replace(/^\/+/, "") ||
        fileNameFromPath(filePath)
    );
}

function isVirtualPackagePath(path: string): boolean {
    return /(^|\/)\.magic-md(\/|$)|\.magic-md(\/|$)/i.test(path.replace(/\\/g, "/"));
}

function buildFilePathExtensionContext(
    path: string,
    kind: "file" | "folder",
    tabId?: string | null,
): MenuContext {
    const context: MenuContext = {
        surface: "file",
        path,
        tabId: tabId ?? undefined,
        targetKind: kind,
    };
    if (isVirtualPackagePath(path)) {
        const virtual = buildVirtualPackageMenuContext({
            name: fileNameFromPath(path),
            path,
            isDirectory: kind === "folder",
            isVirtual: true,
        });
        if (virtual) context.virtual = virtual;
    }
    return context;
}

export async function copyTextWithStatus(
    text: string,
    successMessage: string,
    t: EditorTranslator,
): Promise<void> {
    try {
        await navigator.clipboard.writeText(text);
        useEditorStore.getState().setStatusMessage(successMessage, 2000);
    } catch {
        useEditorStore.getState().setStatusMessage(t("menu.copyFailed"));
    }
}

export async function runExternalPathOpener(
    filePath: string,
    openerId: string,
    t: EditorTranslator,
): Promise<void> {
    const opener =
        openerId === REVEAL_IN_FOLDER.id
            ? REVEAL_IN_FOLDER
            : EXTERNAL_OPENERS.find((item) => item.id === openerId);
    const openExternal = getOptionalOpenExternal();
    if (!opener || !openExternal) {
        useEditorStore.getState().setStatusMessage(t("menu.externalOpenUnsupported"));
        return;
    }
    try {
        await openExternal(opener.url(filePath));
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        useEditorStore
            .getState()
            .setStatusMessage(t("menu.externalOpenFailed", {
                label: opener.label,
                message: message.slice(0, 80),
            }));
    }
}

function isMarkdownDocumentPath(path: string): boolean {
    return /\.md$/i.test(path);
}

export function buildTraceRecordingMenuItem(
    t: EditorTranslator,
    options: { separator?: boolean } = {},
): ContextMenuItem {
    const traceRecording = useTraceStore.getState().recording;
    return {
        key: traceRecording ? "stop-trace" : "start-trace",
        label: traceRecording ? t("trace.stop") : t("trace.start"),
        icon: <Activity size={14} />,
        separator: options.separator,
        onClick: () => {
            ensureTraceNotificationHandlers();
            useTraceStore.getState().setRecording(!traceRecording);
            useLogStore.getState().setActiveTab("trace");
        },
    };
}

export async function ensureDocumentAssetsPackage(
    filePath: string,
    t: EditorTranslator,
): Promise<void> {
    const editor = useEditorStore.getState();
    try {
        const assetsDir = getDocumentAssetsDir(filePath);
        const bundleDir = getDocumentBundleDir(filePath);
        await getPlatformService().engine.workspaceMkdir({
            path: assetsDir,
            recursive: true,
        });
        const parentDir = filePath.substring(0, filePath.lastIndexOf("/"));
        if (parentDir) {
            await editor.refreshSubtree(parentDir);
        } else {
            await editor.refreshFileTree();
        }
        await editor.refreshSubtree(bundleDir);
        await editor.expandTreeNode(filePath);
        useEditorStore.getState().setStatusMessage(t("menu.assetsPackageReady"), 2000);
    } catch (error) {
        const message = error instanceof Error ? error.message : t("common.unknown");
        useEditorStore
            .getState()
            .setStatusMessage(t("menu.createAssetsPackageFailed", { message }), 4000);
    }
}

export interface FilePathMenuOptions {
    path: string;
    t: EditorTranslator;
    kind?: "file" | "folder";
    title?: string;
    includeFileName?: boolean;
    includeRevealInFolder?: boolean;
    includeExternalOpeners?: boolean;
    includeDocumentActions?: boolean;
    includeStar?: boolean;
    includeExtensionActions?: boolean;
    starLabel?: string;
    firstSeparator?: boolean;
}

export function buildFilePathMenuItems({
    path,
    t,
    kind = "file",
    title,
    includeFileName = kind === "file",
    includeRevealInFolder = true,
    includeExternalOpeners = false,
    includeDocumentActions = false,
    includeStar = true,
    includeExtensionActions = true,
    starLabel,
    firstSeparator = false,
}: FilePathMenuOptions): ContextMenuItem[] {
    const items: ContextMenuItem[] = [
        {
            key: "copy-path",
            label: t("menu.copyPath"),
            icon: <Clipboard size={14} />,
            separator: firstSeparator,
            onClick: async () => {
                await useEditorStore.getState().copyPathToClipboard(path);
            },
        },
        {
            key: "copy-relative-path",
            label: t("menu.copyRelativePath"),
            icon: <Link size={14} />,
            onClick: async () => {
                await copyTextWithStatus(
                    getWorkspaceRelativePath(path),
                    t("menu.copiedRelativePath"),
                    t,
                );
            },
        },
    ];

    if (includeFileName) {
        items.push({
            key: "copy-file-name",
            label: t("menu.copyFileName"),
            icon: <Copy size={14} />,
            onClick: async () => {
                await copyTextWithStatus(
                    fileNameFromPath(path) || title || path,
                    t("menu.copiedFileName"),
                    t,
                );
            },
        });
    }

    if (includeRevealInFolder) {
        items.push({
            key: "reveal-in-folder",
            label: REVEAL_IN_FOLDER.label,
            icon: <REVEAL_IN_FOLDER.icon size={14} />,
            separator: true,
            disabled: !getOptionalOpenExternal(),
            onClick: async () => {
                await runExternalPathOpener(path, REVEAL_IN_FOLDER.id, t);
            },
        });
    }

    if (includeExternalOpeners && kind === "file") {
        const canOpenExternal = !!getOptionalOpenExternal();
        items.push({
            key: "open-with",
            label: t("menu.openWith"),
            icon: <ExternalLink size={14} />,
            disabled: !canOpenExternal,
            children: EXTERNAL_OPENERS.map((opener) => ({
                key: `open-external-${opener.id}`,
                label: opener.label,
                icon: <opener.icon size={14} />,
                onClick: async () => {
                    await runExternalPathOpener(path, opener.id, t);
                },
            })),
        });
    }

    if (includeStar) {
        const store = useEditorStore.getState();
        const isStarred = store.isStarred(path);
        items.push({
            key: isStarred ? "unstar" : "star",
            label: isStarred ? t("menu.removeFromFavorites") : t("menu.addToFavorites"),
            icon: isStarred ? <StarOff size={14} /> : <Star size={14} />,
            separator: true,
            onClick: async () => {
                const currentStore = useEditorStore.getState();
                if (currentStore.isStarred(path)) {
                    await currentStore.unstarItem(path);
                    return;
                }
                await currentStore.starItem(path, kind, starLabel ?? title);
            },
        });
    }

    if (includeDocumentActions && kind === "file" && isMarkdownDocumentPath(path)) {
        items.push(buildTraceRecordingMenuItem(t, { separator: true }));
        items.push({
            key: "assets-package",
            label: t("menu.addAssetsPackage"),
            icon: <Package size={14} />,
            onClick: async () => {
                await ensureDocumentAssetsPackage(path, t);
            },
        });
        items.push({
            key: "publish-file",
            label: t("menu.publishDocument"),
            icon: <Upload size={14} />,
            onClick: async () => {
                await showDocumentPublishDialog(path);
            },
        });
        if (hasKnownDocumentPublishPreview(path)) {
            items.push({
            key: "preview-publish",
            label: t("menu.previewPublish"),
                icon: <Eye size={14} />,
                onClick: async () => {
                    await previewDocumentPublish(path);
                },
            });
        }
    }

    if (includeExtensionActions) {
        items.push(
            ...buildExtensionContextMenuItems(
                buildFilePathExtensionContext(path, kind),
                { firstSeparator: true },
            ),
        );
    }

    return items;
}

export interface TabCloseMenuOptions {
    tabId: string;
    t: EditorTranslator;
    tabs: { id: string }[];
    closeTab: (id: string) => void;
    firstSeparator?: boolean;
}

export function buildTabCloseMenuItems({
    tabId,
    t,
    tabs,
    closeTab,
    firstSeparator = false,
}: TabCloseMenuOptions): ContextMenuItem[] {
    const tabIndex = tabs.findIndex((tab) => tab.id === tabId);
    return [
        {
            key: "close",
            label: t("common.close"),
            icon: <X size={14} />,
            separator: firstSeparator,
            onClick: () => closeTab(tabId),
        },
        {
            key: "close-others",
            label: t("menu.closeOthers"),
            icon: <XCircle size={14} />,
            disabled: tabs.length <= 1,
            onClick: () => {
                for (const tab of tabs) {
                    if (tab.id !== tabId) closeTab(tab.id);
                }
            },
        },
        {
            key: "close-to-right",
            label: t("menu.closeToRight"),
            icon: <XCircle size={14} />,
            disabled: tabIndex < 0 || tabIndex >= tabs.length - 1,
            onClick: () => {
                if (tabIndex < 0) return;
                for (const tab of tabs.slice(tabIndex + 1)) {
                    closeTab(tab.id);
                }
            },
        },
        {
            key: "close-all",
            label: t("menu.closeAll"),
            icon: <XCircle size={14} />,
            disabled: tabs.length === 0,
            onClick: () => {
                for (const tab of tabs) {
                    closeTab(tab.id);
                }
            },
        },
    ];
}

function getExtensionOwner(item: ContextMenuItem): string | null {
    const match = /^extension:[^:]+:([^:]+):[^:]+:/.exec(item.key);
    return match?.[1] ?? null;
}

function getExtensionContributionKey(item: ContextMenuItem): string | null {
    const match = /^extension:[^:]+:[^:]+:[^:]+:(.+)$/.exec(item.key);
    return match?.[1] ?? null;
}

function getExtensionSubmenuMergeKey(item: ContextMenuItem): string | null {
    if (!item.children || item.children.length === 0) return null;
    const owner = getExtensionOwner(item);
    return owner ? `${owner}:${item.label}` : null;
}

function mergeContextMenuChildren(
    preferred: ContextMenuItem[],
    fallback: ContextMenuItem[],
): ContextMenuItem[] {
    const merged: ContextMenuItem[] = [];
    const seen = new Set<string>();
    for (const item of [...preferred, ...fallback]) {
        const key = getExtensionContributionKey(item) ?? item.key;
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(item);
    }
    return merged;
}

function mergeFileAndTabExtensionMenuItems(
    fileItems: ContextMenuItem[],
    tabItems: ContextMenuItem[],
): ContextMenuItem[] {
    const merged = [...fileItems];
    const submenuIndexes = new Map<string, number>();
    merged.forEach((item, index) => {
        const key = getExtensionSubmenuMergeKey(item);
        if (key) submenuIndexes.set(key, index);
    });

    for (const tabItem of tabItems) {
        const key = getExtensionSubmenuMergeKey(tabItem);
        const existingIndex = key ? submenuIndexes.get(key) : undefined;
        if (existingIndex === undefined) {
            merged.push(tabItem);
            if (key) submenuIndexes.set(key, merged.length - 1);
            continue;
        }

        const fileItem = merged[existingIndex];
        merged[existingIndex] = {
            ...tabItem,
            key: fileItem.key,
            separator: fileItem.separator || tabItem.separator,
            disabled: fileItem.disabled && tabItem.disabled,
            children: mergeContextMenuChildren(
                tabItem.children ?? [],
                fileItem.children ?? [],
            ),
        };
    }

    if (merged.length > 0) {
        merged[0] = { ...merged[0], separator: true };
    }
    return merged;
}

export interface TabContextMenuOptions {
    tabId: string;
    t: EditorTranslator;
    tabs: { id: string }[];
    closeTab: (id: string) => void;
}

export function buildTabContextMenuItems({
    tabId,
    t,
    tabs,
    closeTab,
}: TabContextMenuOptions): ContextMenuItem[] {
    const tab = useEditorStore.getState().tabs.find((item) => item.id === tabId);
    const filePath = tab?.filePath;
    const items: ContextMenuItem[] = [
        {
            key: "reveal-in-explorer",
            label: t("menu.locateInExplorer"),
            icon: <FolderTree size={14} />,
            disabled: !filePath,
            onClick: async () => {
                if (!filePath) return;
                await revealPathInExplorer(filePath);
            },
        },
    ];

    if (filePath) {
        items.push(
            ...buildFilePathMenuItems({
                path: filePath,
                kind: "file",
                title: tab.title,
                t,
                starLabel: tab.title,
                includeExternalOpeners: true,
                includeDocumentActions: true,
                includeExtensionActions: false,
                firstSeparator: true,
            }),
        );
    }

    const tabExtensionItems = buildExtensionContextMenuItems({
        surface: "tab",
        tabId,
        path: filePath,
    });
    const extensionItems = filePath
        ? mergeFileAndTabExtensionMenuItems(
            buildExtensionContextMenuItems({
                ...buildFilePathExtensionContext(filePath, "file", tabId),
            }),
            tabExtensionItems,
        )
        : tabExtensionItems;
    if (extensionItems.length > 0) {
        extensionItems[0] = { ...extensionItems[0], separator: true };
        items.push(...extensionItems);
    }

    const extras = extraTabMenuItems?.(tabId);
    if (extras && extras.length > 0) {
        extras[0] = { ...extras[0], separator: true };
        items.push(...extras);
    }

    items.push(
        ...buildTabCloseMenuItems({
            tabId,
            t,
            tabs,
            closeTab,
            firstSeparator: true,
        }),
    );

    return items;
}

export interface OpenFileMenuOptions {
    path: string;
    t: EditorTranslator;
    title: string;
    tabId?: string | null;
    tabs?: { id: string }[];
    isCycle?: boolean;
    onOpen: () => void;
    onOpenToSide: () => void;
    closeTab?: (id: string) => void;
}

export function buildOpenFileMenuItems({
    path,
    t,
    title,
    tabId,
    tabs = [],
    isCycle = false,
    onOpen,
    onOpenToSide,
    closeTab,
}: OpenFileMenuOptions): ContextMenuItem[] {
    const items: ContextMenuItem[] = [
        {
            key: "open",
            label: tabId ? t("menu.switchToFile") : t("menu.openFile"),
            icon: <FileText size={14} />,
            disabled: isCycle,
            onClick: onOpen,
        },
        {
            key: "open-to-side",
            label: t("menu.openToSide"),
            icon: <PanelRightOpen size={14} />,
            disabled: isCycle,
            onClick: onOpenToSide,
        },
        ...buildFilePathMenuItems({
            path,
            t,
            kind: "file",
            title,
            includeExternalOpeners: true,
            includeDocumentActions: true,
            firstSeparator: true,
        }),
    ];

    if (tabId && closeTab) {
        items.push(
            ...buildTabCloseMenuItems({
                tabId,
                t,
                tabs,
                closeTab,
                firstSeparator: true,
            }),
        );
    }

    return items;
}
